import React, { useEffect, useState } from 'react';
import { Checkbox, Divider, Modal, Form } from 'antd';
import { Tooltip } from 'antd';
import DOMPurify from 'dompurify';
import { generateNotificationEmailHtml } from '@/utils/emailSMSHelper';
import { WorkspacePremiumOutlined } from '@mui/icons-material';
import { NotifKey } from 'src/enums/ENotifKey';
import { Show } from '@/interfaces/INotifications';
import { OverViewCardForm } from '../../Cards/OverviewCardForm';

interface props {
	open: boolean;
	setOpen: (val: boolean) => void;
	disabled: boolean;
	notifKey: string;
	show: Show | null;
	onShowChange?: (newShow: Show) => void;
	resellerName?: string;
	resellerLogo?: string;
	resellerClient?: boolean;
}

const PreviewEmail: React.FC<props> = ({
	open,
	setOpen,
	notifKey,
	show: parentShow,
	onShowChange,
	resellerName,
	resellerLogo,
	resellerClient,
}) => {
	const [form] = Form.useForm();
	const [localShow, setLocalShow] = useState<Show | null>(parentShow);
	const [htmlPart, setHtmlPart] = useState<string>('');

	useEffect(() => {
		if (parentShow) {
			setLocalShow(parentShow);
			form.setFieldsValue({ show: parentShow });
		}
	}, [parentShow, form]);

	useEffect(() => {
		const emailHtml = generateNotificationEmailHtml({
			show:
				notifKey === NotifKey.DirectSMS ||
				notifKey === NotifKey.Voicemail ||
				notifKey === NotifKey.CRM ||
				notifKey === NotifKey.Calendar ||
				notifKey === NotifKey.Robocall
					? {
							transcript: false,
							callSummary: false,
							intakeForms: false,
							workflow: false,
							callSummaryText: false,
							callInformationLink: false,
						}
					: localShow,
			notifKey,
			resellerName,
			resellerLogo,
			resellerClient,
		});
		setHtmlPart(emailHtml);
	}, [localShow]);

	const sanitizeAndFormatText = (text: string) => {
		const sanitized = DOMPurify.sanitize(text);
		return sanitized;
	};

	const handleSubmit = async () => {
		if (onShowChange && localShow) {
			onShowChange(localShow);
		}
		setOpen(false);
	};

	const onLocalCheck = (key: keyof Show) => {
		if (localShow) {
			const newShow = {
				...localShow,
				[key]: !localShow[key],
			};
			setLocalShow(newShow);
			form.setFieldsValue({ show: newShow });
		}
	};

	const FormItems = () => (
		<div className="flex flex-col">
			{notifKey === NotifKey.DirectCall && (
				<>
					<div className="flex items-center gap-4">
						<span className="mr-4">Include:</span>
						<span>
							<Form.Item name={['show', 'workflow']} valuePropName="checked" noStyle>
								<Checkbox onChange={() => onLocalCheck('workflow')}>Workflow</Checkbox>
							</Form.Item>
						</span>
						<span>
							<Form.Item name={['show', 'callSummary']} valuePropName="checked" noStyle>
								<Checkbox onChange={() => onLocalCheck('callSummary')}>Call Summary</Checkbox>
							</Form.Item>
						</span>
						<span>
							<Form.Item name={['show', 'transcript']} valuePropName="checked" noStyle>
								<Checkbox onChange={() => onLocalCheck('transcript')}>Transcript</Checkbox>
							</Form.Item>
						</span>
						<span>
							<Form.Item name={['show', 'intakeForms']} valuePropName="checked" noStyle>
								<Checkbox onChange={() => onLocalCheck('intakeForms')}>Intake Forms</Checkbox>
							</Form.Item>
						</span>
					</div>
					<Divider />
				</>
			)}
			<div>
				<span
					dangerouslySetInnerHTML={{
						__html: sanitizeAndFormatText(htmlPart),
					}}
				/>
			</div>
		</div>
	);

	return (
		<Modal
			title={
				<div className="flex items-center gap-1">
					<span className="text-lg">
						{notifKey === NotifKey.DirectCall
							? 'Customize Email Notification'
							: 'Preview Email Notification'}
					</span>
					<Tooltip title="Included from Basic plan">
						<WorkspacePremiumOutlined fontSize="medium" className="text-blue-500 cursor-pointer" />
					</Tooltip>
				</div>
			}
			open={open}
			onCancel={() => setOpen(false)}
			footer={null}
			width={700}
		>
			<OverViewCardForm
				form={form}
				initialValues={{
					show: parentShow,
				}}
				formItems={<FormItems />}
				handleSubmit={handleSubmit}
			/>
		</Modal>
	);
};

export default PreviewEmail;
